/* john-widget.js — John Sheridan floating chat widget for uni-trendus.com
 * Dependency-free. Reads window.JOHN_CONFIG = { apiUrl, turnstileSiteKey? }.
 * Injects its own styles, renders a bottom-right bubble + chat panel, and
 * talks to the public FastAPI endpoint (POST {apiUrl}/api/chat).
 *
 * No framework, no build step. Safe to load once per page (guards against
 * double-injection). Session id persists per browser tab via sessionStorage.
 */
(function () {
  "use strict";
  if (window.__johnWidgetLoaded) return;
  window.__johnWidgetLoaded = true;

  var CFG = window.JOHN_CONFIG || {};
  var API = (CFG.apiUrl || "").replace(/\/+$/, "");
  var TURNSTILE_KEY = CFG.turnstileSiteKey || "";
  if (!API) { console.warn("[john] JOHN_CONFIG.apiUrl not set — widget disabled"); return; }

  // ── Brand tokens (match app.py) ──────────
  // ── BUILD STAMP ───────────────────────────────────────────────────────────────
  // 🔴 2026-09-02d — THE WIDGET COULD NOT SAY WHICH BUILD IT WAS, AND THAT COST A DAY.
  // John ships as TWO independently-deployed halves: this file, which is a THEME ASSET
  // saved per-theme in Shopify admin, and generate.py/server.py, which are a CLOUD RUN
  // revision. They move separately and nothing forced them to agree. On 2026-09-02 the
  // newest asset sat on MAIN while the page being previewed was a DRAFT holding the
  // previous one, and the only way to tell was to read the footer copy and infer.
  // Twice the conclusion drawn was "the fix failed" when the fix was simply not loaded.
  //
  // 🔑 A SURFACE THAT CANNOT NAME ITS OWN VERSION TURNS EVERY STALE CACHE INTO A BUG
  // REPORT. Bump WIDGET_BUILD in the same edit as any change to this file — a stamp that
  // lags is worse than none, because it is believed.
  //
  // 2026-10-21 (Pete): "pull the beta designation and the pill color off the widget". The
  // BETA pill and its amber build-mismatch colour are gone. The two halves are now versioned
  // INDEPENDENTLY: WIDGET_BUILD moves only when THIS FILE changes, and a server-only build no
  // longer needs a theme upload just to keep the pill grey. The diagnostic moved off the
  // customer's screen and into the console: on first open the widget logs its own build,
  // the server's build and its fingerprint (stampServerBuild below). It still never guesses.
  var WIDGET_BUILD = "2026.10.21-b";
  window.JOHN_WIDGET_BUILD = WIDGET_BUILD;
  try { console.info("John widget build " + WIDGET_BUILD); } catch (e) {}

  var C = { ink: "#1a1a2e", accent: "#0066cc", link: "#1a73e8", soft: "#f8f9fb", line: "#e2e6ea", muted: "#6c757d",
           // 2026-09-02d — the composer needs its own colours, not the hairline `line`.
           // MEASURED: `line` (#e2e6ea) against the white footer is 1.25:1. WCAG 2.2
           // 1.4.11 requires 3:1 for the boundary of an input, so the box a customer is
           // meant to type into was effectively invisible — Pete flagged it by eye first.
           // `inputLine` clears 3:1 against BOTH adjacent fills (3.64 vs #fff, 3.28 vs
           // the tinted footer); the tint gives a second, non-colour-dependent cue.
           inputLine: "#7d8794", footBg: "#f1f3f6" };

  // ── Session id (per tab) ──────────
  var SID_KEY = "john_sid";
  // 2026-09-16c (Pete: "can he read what's on the card on the site? That's already gated and
  // realtime"). The product page's own stock line, e.g. "2 in stock" -- the storefront count,
  // which already leaves out Amazon FBA. Product pages only; null anywhere else.
  function pageStock() {
    try {
      if (!/\/products\//.test(location.pathname)) return null;
      var el = document.querySelector(".product__inventory");
      var t = el ? (el.textContent || "").replace(/\s+/g, " ").trim() : "";
      return t ? t.slice(0, 80) : null;
    } catch (e) { return null; }
  }

  function sid() {
    var s = null;
    try { s = sessionStorage.getItem(SID_KEY); } catch (e) {}
    if (!s) {
      s = (crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()).replace(/-/g, "");
      try { sessionStorage.setItem(SID_KEY, s); } catch (e) {}
    }
    return s;
  }
  var turnIndex = 0;

  // 🔴 2026-09-03e — THE BROWSER IS THE STATE. John is stateless by construction:
  // generate_answer builds `messages` from ONE user turn, so the referent of "it" — which
  // lives in the previous turn — is never sent. Measured live 2026-09-03: turn 1 "what is
  // the total wattage of the UDP4303S?" answered correctly at top_sim 1.000; turn 2 "is it
  // TRMS?" returned a fluent, confident answer about the UT8806E, a handheld multimeter the
  // customer never mentioned. Worse than a deferral, because it does not announce itself.
  //
  // 🔑 THE SERVER ALREADY SENDS THIS BACK. `availability.model` and `products[].model` arrive
  // on every product turn and were being rendered and thrown away. Keeping one string is the
  // whole fix — no session store (Cloud Run instances are not sticky), no answer_log read on
  // the hot path, no paid LLM rewrite per follow-up. Same shape as the turnIndex above.
  var lastModel = null;
  // 🆕 2026-09-10b — A LIST HAS NO "IT". After "which of your scopes have 500 MHz?" the answer
  // names five models, and carrying the first card's model into "is it in stock?" would answer
  // about one the customer never picked. The server flags such answers `list_answer`; the
  // widget then forgets `lastModel` and remembers the LIST, which it sends back as
  // `candidate_models` so the server can ask "which one?" with one-tap choices.
  var lastList = [];
  // 2026-09-11 (post-sitewide) — A REQUIREMENT SAID ACROSS TURNS. On the home page a shopper
  // narrows in steps: "what scopes do you sell?", "at least 1 GHz", "mixed signal". The server
  // returns the filter it built as `filter_state`; the widget holds it and sends it back as
  // `carried_filter`, exactly like the list above. Opaque here: the server validates every field.
  // Live defect it fixes: the third step answered with three 200-500 MHz scopes (L-2026-09-11-6).
  var lastFilter = null;

  // ── Styles ──────────
  var css = "" +
    "#john-fab{position:fixed;right:20px;bottom:20px;z-index:2147483000;display:flex;align-items:center;gap:8px;" +
      "background:" + C.accent + ";color:#fff;border:none;border-radius:28px;padding:12px 18px;font:600 14px/1 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;" +
      "box-shadow:0 6px 20px rgba(0,0,0,.22);cursor:pointer;transition:transform .15s ease,box-shadow .15s ease}" +
    "#john-fab:hover{transform:translateY(-1px);box-shadow:0 8px 26px rgba(0,0,0,.28)}" +
    "#john-fab svg{width:20px;height:20px;display:block}" +
    "#john-panel{position:fixed;right:20px;bottom:20px;z-index:2147483001;width:380px;max-width:calc(100vw - 32px);height:560px;max-height:calc(100vh - 32px);" +
      "background:#fff;border-radius:14px;box-shadow:0 16px 48px rgba(0,0,0,.30);display:none;flex-direction:column;overflow:hidden;" +
      "font:14px/1.5 system-ui,-apple-system,Segoe UI,Roboto,sans-serif}" +
    "#john-panel.open{display:flex}" +
    "#john-hd{background:" + C.ink + ";color:#fff;padding:12px 14px;display:flex;align-items:center;gap:10px}" +
    "#john-hd .nm{font-weight:700;font-size:15px;letter-spacing:.02em}" +
    "#john-hd .rl{font-size:11px;color:#aab4d4}" +
    "#john-hd .cl{background:transparent;border:none;color:#aab4d4;font-size:20px;line-height:1;cursor:pointer;padding:0 2px}" +
    "#john-hd .cl:hover{color:#fff}" +
    "#john-log{flex:1;overflow-y:auto;padding:14px;background:" + C.soft + "}" +
    "#john-log .row{display:flex;margin-bottom:10px}" +
    "#john-log .row.u{justify-content:flex-end}" +
    "#john-log .b{position:relative;max-width:82%;padding:9px 12px;border-radius:12px;white-space:pre-wrap;word-wrap:break-word;overflow-wrap:anywhere}" +
    // 2026-09-10b, measured on the live MSO2102 page after the deploy: the store's product-label app
    // ("Free Options" ribbons, class aco-box, z-index 9999) scans EVERY product image on the page,
    // found the MSO3054X image inside a John card, tagged the card and inserted its ribbon into the
    // card ROW. Nothing between that row and #john-panel was positioned, so the ribbon anchored to
    // the panel itself — offsetParent john-panel, x=0 y=0 — and sat over the header. Two rules:
    // hide that app's label inside John, and make every bubble a positioning context (above) so the
    // NEXT app that injects an absolute element lands inside the answer it belongs to, not on the
    // chrome. The product page's own ribbons are untouched.
    "#john-panel .aco-box{display:none!important}" +
    "#john-log .row.a .b{background:#fff;border:1px solid " + C.line + ";color:#1c1c28;border-top-left-radius:4px}" +
    "#john-log .row.u .b{background:" + C.accent + ";color:#fff;border-top-right-radius:4px}" +
    "#john-log .b a{color:" + C.link + ";text-decoration:underline}" +
    "#john-log .row.u .b a{color:#fff}" +
    // 2026-09-10b one-tap choices under an answer. Buttons, not links: a click SENDS a turn.
    "#john-log .chips{display:flex;flex-wrap:wrap;gap:6px;margin:-4px 0 10px 0}" +
    "#john-log .chip{background:#fff;border:1px solid " + C.accent + ";color:" + C.accent + ";border-radius:16px;padding:5px 11px;font:600 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;cursor:pointer}" +
    "#john-log .chip:hover,#john-log .chip:focus{background:" + C.accent + ";color:#fff;outline:none}" +
    "#john-log .chips.used .chip{opacity:.45;cursor:default;pointer-events:none}" +
    "#john-log .typing{display:inline-flex;gap:4px;align-items:center;padding:4px 2px}" +
    "#john-log .typing i{width:6px;height:6px;border-radius:50%;background:" + C.muted + ";opacity:.5;animation:johnb 1s infinite}" +
    "#john-log .typing i:nth-child(2){animation-delay:.15s}#john-log .typing i:nth-child(3){animation-delay:.3s}" +
    "@keyframes johnb{0%,100%{opacity:.3;transform:translateY(0)}50%{opacity:1;transform:translateY(-3px)}}" +
    "#john-ts{padding:0 14px}" +
    "#john-ft{border-top:1px solid " + C.line + ";padding:10px;background:" + C.footBg + "}" +
    "#john-ft form{display:flex;gap:8px;align-items:flex-end}" +
    "#john-ft textarea{flex:1;resize:none;background:#fff;border:1px solid " + C.inputLine + ";border-radius:10px;padding:9px 11px;font:14px/1.4 inherit;max-height:96px;outline:none}" +
    // resize:none already stops the drag, but Chrome still paints the grip on some
    // platforms; ::-webkit-resizer removes the artifact Pete saw in the corner.
    "#john-ft textarea::-webkit-resizer{display:none}" +
    "#john-ft textarea::placeholder{color:" + C.muted + ";opacity:1}" +
    "#john-ft textarea:focus{border-color:" + C.accent + ";box-shadow:0 0 0 3px rgba(0,102,204,.18)}" +
    "#john-ft button{background:" + C.accent + ";color:#fff;border:none;border-radius:10px;padding:0 14px;height:38px;font:600 14px/1 inherit;cursor:pointer}" +
    "#john-ft button:disabled{opacity:.5;cursor:default}" +
    "#john-dis{font-size:11px;color:" + C.muted + ";text-align:center;padding:6px 10px 2px}" +
  // ── End chat + session rating (2026-09-03g) ──────────
  // The End button is a QUIET control on purpose. It sits left of the ×, in the header
  // muted colour, at 11px: ending a chat is a deliberate act, not something to invite by
  // making it as loud as Send. Underlined rather than boxed so it never reads as the
  // primary action of the panel.
  "#john-hd .endbtn{margin-left:auto;background:transparent;border:none;color:#aab4d4;font:500 11px/1 inherit;cursor:pointer;padding:2px 4px;text-decoration:underline;text-underline-offset:2px}" +
  "#john-hd .endbtn:hover{color:#fff}" +
  "#john-log .sys{text-align:center;color:" + C.muted + ";font-size:12px;margin:6px 0 10px}" +
  "#john-rate{background:#fff;border:1px solid " + C.line + ";border-radius:12px;padding:14px 12px 12px;margin:4px 0 8px;position:relative;text-align:center}" +
  "#john-rate .rt{font-size:14px;color:#1c1c28;margin-bottom:10px}" +
  "#john-rate .faces{display:flex;justify-content:center;gap:22px;margin-bottom:8px}" +
  // 44px is the WCAG 2.2 2.5.8 target-size minimum. The emoji glyph is 26px; the button
  // around it is padded out to 44 so a thumb on a phone hits the face it aimed at.
  "#john-rate .face{background:transparent;border:none;cursor:pointer;font-size:26px;line-height:1;min-width:44px;min-height:44px;border-radius:10px;padding:0;transition:transform .12s ease,background .12s ease}" +
  "#john-rate .face:hover{transform:scale(1.15);background:" + C.soft + "}" +
  "#john-rate .face:focus-visible{outline:2px solid " + C.accent + ";outline-offset:2px}" +
  "#john-rate .face[aria-pressed=\"true\"]{background:" + C.soft + ";transform:scale(1.15)}" +
  "#john-rate .cap{font-size:13px;color:" + C.muted + ";line-height:1.4}" +
  "#john-rate .dis{position:absolute;top:6px;right:8px;background:transparent;border:none;color:" + C.muted + ";font-size:18px;line-height:1;cursor:pointer;padding:2px 4px}" +
  "#john-rate .dis:hover{color:#1c1c28}" +
  "#john-rate .cmt{display:none;margin-top:10px}" +
  "#john-rate .cmt.show{display:block}" +
  "#john-rate .cmt textarea{width:100%;box-sizing:border-box;resize:none;background:#fff;border:1px solid " + C.inputLine + ";border-radius:10px;padding:9px 11px;font:14px/1.4 inherit;min-height:64px;max-height:120px;outline:none}" +
  "#john-rate .cmt textarea:focus{border-color:" + C.accent + ";box-shadow:0 0 0 3px rgba(0,102,204,.18)}" +
  "#john-rate .cmt .go{margin-top:6px;background:" + C.accent + ";color:#fff;border:none;border-radius:10px;padding:0 14px;height:34px;font:600 13px/1 inherit;cursor:pointer}" +
  "#john-rate .cmt .go:disabled{opacity:.5;cursor:default}" +
  "#john-resume{text-align:center;color:" + C.muted + ";font-size:12px;margin:2px 0 8px}" +
  "#john-resume a{color:" + C.link + ";text-decoration:underline;cursor:pointer}" +
    "@media (max-width:480px){#john-panel{right:0;bottom:0;width:100vw;height:100vh;max-width:100vw;max-height:100vh;border-radius:0}}";

  var style = document.createElement("style");
  style.textContent = css;
  document.head.appendChild(style);

  // ── DOM ──────────
  var fab = document.createElement("button");
  fab.id = "john-fab";
  fab.setAttribute("aria-label", "Ask John — UNI-T pre-sales assistant");
  fab.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>' +
    '<span>Ask John</span>';

  var panel = document.createElement("div");
  panel.id = "john-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "John Sheridan chat");
  panel.innerHTML =
    '<div id="john-hd">' +
      '<div><div class="nm">John Sheridan</div>' +
        '<div class="rl">UNI-T Pre-Sales Assistant</div></div>' +
      '<button class="endbtn" id="john-end" aria-label="End this chat session">End chat</button>' +
      '<button class="cl" aria-label="Close chat">×</button>' +
    '</div>' +
    '<div id="john-log"></div>' +
    '<div id="john-ts"></div>' +
    '<div id="john-ft">' +
      '<div id="john-dis">Answers may be imperfect. <br>For orders &amp; pricing, call 888-668-8648.</div>' +
      '<form autocomplete="off">' +
        '<textarea rows="1" placeholder="Ask about a model or spec…" aria-label="Your question"></textarea>' +
        '<button type="submit">Send</button>' +
      '</form>' +
    '</div>';

  document.body.appendChild(fab);
  document.body.appendChild(panel);

  var logEl = panel.querySelector("#john-log");
  var form = panel.querySelector("form");
  var ta = panel.querySelector("textarea");
  var sendBtn = panel.querySelector('button[type="submit"]');
  var tsHost = panel.querySelector("#john-ts");
  var endBtn = panel.querySelector("#john-end");

  // ── Linkify (presentational; ports the server-side idea to the browser) ──────────
  function esc(s) { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
  var LINK_RE = /(\bhttps?:\/\/[^\s<>()]+)|([\w.+-]+@[\w-]+(?:\.[\w-]+)+)|((?:[a-z0-9-]+\.)+(?:com|net|org|io)(?:\/[^\s<>()]*)?)/gi;
  var TRAIL = /[.,;:!?'")\]}—–]+$/;

  // 🔴 2026-09-02d — MARKDOWN WAS NEVER RENDERED HERE, AND THE ANSWERS ARE MARKDOWN.
  // generate.py composes every answer with markdown links (`_md_link()`) and bold
  // model names, and SYSTEM_PROMPT's link-formatting rule instructs the model to do
  // the same. Streamlit rendered it, so on app.py it looked right; this widget only
  // ever linkified BARE urls, so a customer saw the raw source:
  //     Yes — the [MSO5104HD](https://uni-trendus.com/products/mso5104hd) is available
  // ...with the url as the visible link text and the product name in brackets beside
  // it. 🔑 The answer layer and the render layer disagreed about the format, and
  // nothing owned the seam — the same shape as `availability.message` being printed
  // by a surface whose contract said it was unrendered.
  var MD_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
  var MD_BOLD_RE = /\*\*([^*\n]+)\*\*/g;

  function anchor(href, label) {
    return '<a href="' + href + '" target="_blank" rel="noopener nofollow">' + label + "</a>";
  }

  function linkify(raw) {
    var text = esc(raw || "");

    // ORDER IS LOAD-BEARING. Markdown links are extracted to placeholders FIRST,
    // because LINK_RE would otherwise match the href inside the parentheses and
    // build an anchor inside an anchor. \u0000 cannot appear in the escaped text
    // or in an answer, so it is a safe fence.
    var held = [];
    text = text.replace(MD_LINK_RE, function (m, label, href) {
      held.push(anchor(href, label));
      return "\u0000" + (held.length - 1) + "\u0000";
    });

    text = text.replace(MD_BOLD_RE, "<strong>$1</strong>");

    text = text.replace(LINK_RE, function (m, url, email, bare) {
      if (email) return '<a href="mailto:' + email + '">' + email + "</a>";
      var t = "", target = m, mt = target.match(TRAIL);
      if (mt) { t = mt[0]; target = target.slice(0, -t.length); }
      var href = url ? target : "https://" + target;
      return anchor(href, target) + t;
    });

    return text.replace(/\u0000(\d+)\u0000/g, function (m, i) { return held[+i]; });
  }

  function addMsg(role, html) {
    var row = document.createElement("div");
    row.className = "row " + (role === "user" ? "u" : "a");
    var b = document.createElement("div");
    b.className = "b";
    b.innerHTML = html;
    row.appendChild(b);
    logEl.appendChild(row);
    logEl.scrollTop = logEl.scrollHeight;
    return row;
  }

  // 2026-09-10b — one-tap choices. Each chip SENDS its `message` as the customer's next turn,
  // exactly as if typed: the server wrote every message as a question it already answers.
  // A used row greys out so an old choice cannot be sent twice from further up the log.
  function renderChips(items) {
    var wrap = document.createElement("div");
    wrap.className = "chips";
    items.slice(0, 5).forEach(function (it) {
      if (!it || !it.label || !it.message) return;
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "chip";
      btn.textContent = String(it.label).slice(0, 40);
      btn.addEventListener("click", function () {
        if (busy) return;
        wrap.classList.add("used");
        send(String(it.message).slice(0, 300));
      });
      wrap.appendChild(btn);
    });
    if (!wrap.childNodes.length) return;
    logEl.appendChild(wrap);
    logEl.scrollTop = logEl.scrollHeight;
  }

  var greeted = false;
  function greet() {
    if (greeted) return; greeted = true;
    addMsg("assistant", "Hi — I’m John, UNI-T’s pre-sales assistant. Ask me about instrument specs, compatibility, programming, or which product fits your application.");
  }

  // ── Turnstile (optional) ──────────
  //
  // 🔴 2026-09-03e — TWO DEFECTS FIXED HERE, BOTH INVISIBLE UNTIL `TURNSTILE_SECRET` IS SET.
  // `_turnstile_ok()` on the server fails OPEN while the secret is unset, so every path below
  // has always "worked" — the token has simply never been checked. The moment the secret goes
  // on, an empty token is a 403 on the customer's screen.
  //
  //   1. `tsReady` was written in three places and READ IN NONE. `send()` took whatever
  //      `turnstileToken()` returned, including "", and posted it.
  //   2. `turnstile.render()` targeted `#john-ts`, which lives inside `#john-panel` —
  //      `display:none` until the FAB is clicked. Cloudflare is unreliable rendering into a
  //      hidden host, so the challenge could fail to run at all, and re-running it later was
  //      nobody's job.
  //
  // 🔑 AND `turnstileReset()` IN `send()`'s `finally` MAKES THIS STRUCTURAL, NOT A RACE. A
  // Turnstile token is single-use, so the reset after every turn is correct — but it means
  // turn 2 onward ALWAYS starts with no token. Without a wait, enabling the secret would 403
  // every multi-turn conversation, which is most of the ones that matter.
  var tsWidgetId = null, tsReady = false, tsApiReady = false, tsRendered = false;

  function renderTurnstile() {
    // Never render into a hidden host: that is defect 2, and calling this from open() is the
    // fix. Guarded so repeated open/close cycles render exactly once.
    if (!TURNSTILE_KEY || tsRendered || !tsApiReady) return;
    if (!panel.classList.contains("open")) return;
    try {
      tsRendered = true;
      tsWidgetId = window.turnstile.render(tsHost, {
        sitekey: TURNSTILE_KEY, size: "flexible", theme: "light",
        callback: function () { tsReady = true; }
      });
    } catch (e) {
      console.warn("[john] turnstile render failed", e);
      tsRendered = false; tsReady = true;
    }
  }

  function initTurnstile() {
    if (!TURNSTILE_KEY) { tsReady = true; return; }
    // Load the API at page load so it is warm, but DEFER the render until the panel opens.
    window.__johnTsOnload = function () { tsApiReady = true; renderTurnstile(); };
    var s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__johnTsOnload&render=explicit";
    s.async = true; s.defer = true;
    document.head.appendChild(s);
  }
  function turnstileToken() {
    if (!TURNSTILE_KEY) return "";
    try { return window.turnstile ? (window.turnstile.getResponse(tsWidgetId) || "") : ""; } catch (e) { return ""; }
  }
  // Wait, briefly, for a token rather than posting an empty one. Returns "" on timeout and
  // lets the request through: the SERVER is the authority on whether a turn is allowed, and a
  // clean 403 with its own message beats the widget inventing a refusal of its own.
  async function awaitTurnstileToken(maxMs) {
    if (!TURNSTILE_KEY) return "";
    var t = turnstileToken();
    if (t) return t;
    var started = Date.now();
    while (Date.now() - started < maxMs) {
      await new Promise(function (r) { setTimeout(r, 150); });
      t = turnstileToken();
      if (t) return t;
    }
    console.warn("[john] turnstile token not ready after " + maxMs + "ms; posting without one");
    return "";
  }
  function turnstileReset() {
    if (TURNSTILE_KEY && window.turnstile) {
      try { window.turnstile.reset(tsWidgetId); tsReady = false; } catch (e) {}
    }
  }

  // ── Send ──────────
  var busy = false;
  function setBusy(v) { busy = v; sendBtn.disabled = v; ta.disabled = v; }

  function showTyping() {
    var row = document.createElement("div");
    row.className = "row a"; row.id = "john-typing";
    row.innerHTML = '<div class="b"><span class="typing"><i></i><i></i><i></i></span></div>';
    logEl.appendChild(row); logEl.scrollTop = logEl.scrollHeight;
  }
  function hideTyping() { var t = document.getElementById("john-typing"); if (t) t.remove(); }

  // 2026-09-10b — Server-Sent Events reader for {"stream": true}. Three events, in order:
  //   delta  {text}              checked sentences, appended as they arrive
  //   answer {answer, deferred}  the FINISHED text — it REPLACES the deltas, always
  //   final  {…the JSON body…}   cards, chips, carry state; the stream ends
  // 🔑 The server decides the shape, not this file: a JSON Content-Type (an older server, or
  // STREAM_ENABLED=0, or a 403/429/503) goes down the JSON branch unchanged. A browser whose
  // fetch has no readable body gets the whole event text at the end — the same answer, just
  // not early. Nothing ever sends the question twice.
  async function readEvents(res, onEvent) {
    var buf = "";
    function handle(block) {
      var ev = "message", lines = [];
      block.split("\n").forEach(function (line) {
        if (!line || line.charAt(0) === ":") return;
        if (line.indexOf("event:") === 0) ev = line.slice(6).trim();
        else if (line.indexOf("data:") === 0) lines.push(line.slice(5).replace(/^ /, ""));
      });
      if (!lines.length) return;
      var payload;
      try { payload = JSON.parse(lines.join("\n")); } catch (e) { return; }
      onEvent(ev, payload);
    }
    function drain(flush) {
      var i;
      while ((i = buf.indexOf("\n\n")) !== -1) { handle(buf.slice(0, i)); buf = buf.slice(i + 2); }
      if (flush && buf.trim()) { handle(buf); buf = ""; }
    }
    if (res.body && res.body.getReader && window.TextDecoder) {
      var reader = res.body.getReader(), dec = new TextDecoder();
      for (;;) {
        var r = await reader.read();
        if (r.done) break;
        buf += dec.decode(r.value, { stream: true }).replace(/\r/g, "");
        drain(false);
      }
      buf += dec.decode().replace(/\r/g, "");
      drain(true);
    } else {
      buf = (await res.text()).replace(/\r/g, "");
      drain(true);
    }
  }

  async function send(text) {
    if (busy || !text.trim()) return;
    addMsg("user", esc(text));
    setBusy(true); showTyping();
    // Bounded wait, not a bare read — see the Turnstile block above. 6s is generous for a
    // Managed challenge and is spent under the typing indicator, so it reads as thinking
    // rather than as a hang.
    var token = await awaitTurnstileToken(6000);
    try {
      var res = await fetch(API + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // carried_model — the model resolved on the PREVIOUS turn, echoed back so the server
        //   can collapse a pronoun follow-up into a self-contained question before anything is
        //   embedded. Validated against the catalog server-side; a junk value degrades only
        //   this user's own answer and can never point John at a model that does not exist.
        // page_context — 🔴 answer_log.page_context was NULL on 356 of 356 rows since Sept 1,
        //   and this line is one of the three reasons why. generate.ask() has ALWAYS accepted
        //   the field; the 58 non-null rows on record are all >14 days old, from the HF Space
        //   embed which supplied it through the iframe query string. When the surface moved to
        //   Cloud Run, the widget stopped sending it, ChatIn had no field for it and server.py
        //   passed no argument — three halves missing, none of them erroring. It never stopped
        //   working; the new surface never carried it.
        body: JSON.stringify({ message: text, session_id: sid(), turn_index: turnIndex++, turnstile_token: token, carried_model: lastModel, page_context: location.pathname, page_stock: pageStock(), candidate_models: lastList.length ? lastList : null, carried_filter: lastFilter, stream: true })
      });
      var data = null, arow = null, streamed = "";
      var bubbleText = function (t) {
        if (!arow) { hideTyping(); arow = addMsg("assistant", ""); }
        arow.querySelector(".b").innerHTML = linkify(t);
        logEl.scrollTop = logEl.scrollHeight;
      };
      if ((res.headers.get("content-type") || "").toLowerCase().indexOf("text/event-stream") !== -1) {
        await readEvents(res, function (ev, p) {
          if (ev === "delta" && p && typeof p.text === "string") { streamed += p.text; bubbleText(streamed); }
          else if (ev === "answer" && p && typeof p.answer === "string") { bubbleText(p.answer); }
          else if (ev === "final") { data = p; }
        });
        // A stream that ended without `final` is a failed turn, even if sentences arrived:
        // they were a preview the guards never finished checking, so they are not left as
        // the answer.
        if (!data) throw new Error("stream ended early");
      } else {
        data = await res.json();
      }
      hideTyping();
      // The finished answer, set once more whatever arrived before it — the deltas never win.
      if (arow) arow.querySelector(".b").innerHTML = linkify(data && data.answer ? data.answer : "Sorry, something went wrong. Please try again.");
      else arow = addMsg("assistant", linkify(data && data.answer ? data.answer : "Sorry, something went wrong. Please try again."));
      // Additive product cards / availability tree (unknown-field-safe; no-op if
      // john-widget-cards.js isn't loaded or the payload is empty).
      // Keep the model the server just resolved, for the next turn's "it". Deliberately in
      // its own try/catch AHEAD of the card render: this must survive john-widget-cards.js
      // being absent or throwing, because the carry-over is an ANSWER-correctness feature and
      // the cards are decoration. Folding it into the block below would make a cosmetic
      // failure silently reintroduce the wrong-product bug.
      try {
        if (data && data.list_answer) {
          lastModel = null;
          lastList = (data.listed_models || []).slice(0, 8);
        } else if (data) {
          var m = (data.availability && data.availability.model) ||
                  (data.products && data.products.length && data.products[0].model) || null;
          if (m) { lastModel = m; lastList = []; }
        }
      } catch (e) { /* never break the answer over a remembered string */ }
      try {
        // Only a server that knows the field sends it; an older one leaves the held filter alone.
        if (data && Object.prototype.hasOwnProperty.call(data, "filter_state")) lastFilter = data.filter_state || null;
      } catch (e) { /* the filter is a shortcut; the question still gets answered */ }
      try { if (data && data.suggestions && data.suggestions.length) renderChips(data.suggestions); }
      catch (e) { /* chips are a shortcut; typing still works */ }
      try {
        if (window.JohnCards && data) {
          var bubble = arow.querySelector(".b");
          if (data.availability) window.JohnCards.renderAvailability(bubble, data.availability);
          else if (data.products && data.products.length) window.JohnCards.renderProducts(bubble, data.products);
        }
      } catch (e) { /* cards are best-effort; never break the answer */ }
    } catch (e) {
      hideTyping();
      if (arow) arow.remove();      // never leave an unfinished preview standing as an answer
      addMsg("assistant", "I couldn’t reach the server just now. Please try again in a moment, or visit uni-trendus.com.");
    } finally {
      setBusy(false); turnstileReset(); ta.focus();
    }
  }

  // ── Wiring ──────────
  // The server half is deployed separately, so the widget ASKS which build is answering
  // rather than assuming they match, and logs both to the console (no visible pill since
  // 2026-10-21).
  // One call, on first open, best-effort. /health is public and does not touch the LLM.
  var _stampedServer = false;
  function stampServerBuild() {
    if (_stampedServer) return;
    _stampedServer = true;
    try {
      fetch(API + "/health", { method: "GET" })
        .then(function (r) { return r.json(); })
        .then(function (h) {
          if (!h) return;
          // Console only (2026-10-21): FULL, LABELLED values, so "which one is which" is never
          // a guess. Widget and server builds are allowed to differ now; read both here.
          try {
            console.info("John builds \u2014 widget " + WIDGET_BUILD + " \u00b7 server "
                         + (h.build || "?")
                         + (h.build_fingerprint ? " \u00b7 fingerprint " + h.build_fingerprint : ""));
          } catch (e) {}
        })
        .catch(function () {});
    } catch (e) {}
  }

  // ── End chat + session rating (2026-09-03g) ───────────────────────────────────
  //
  // 🔑 THIS IS THE FEATURE THAT GIVES JOHN A SESSION LIFECYCLE, NOT A BUTTON ON TOP OF
  // ONE. Before this, the × was a MINIMISE and nothing ended anything: sid() lives in
  // sessionStorage for the life of the tab and turnIndex just kept counting. close()'s own
  // comment below says sid() and turnIndex are 'deliberately left alone: that is existing
  // behaviour and a separate decision'. This is that decision, made by Pete 2026-09-03g.
  // The × is UNCHANGED and still minimises.
  //
  // 🔴 THE SID IS CAPTURED BEFORE IT IS ROTATED, AND THAT ORDER IS THE WHOLE FEATURE.
  // The rating belongs to the session that just ended. Rotate first and every rating
  // attaches to a brand-new session with zero answers in it — the endpoint would refuse
  // them all (no answer_log rows for that sid) and the table would sit empty while every
  // part of the UI looked correct. That is the shape of answer_log.page_context reading
  // NULL on 356 of 356 rows: an instrument that is absent looks exactly like one reading
  // zero. endedSid is also persisted, so the card still submits after a page navigation.
  var ENDED_KEY = "john_ended_sid";
  var ended = false;

  function endedSid() {
    try { return sessionStorage.getItem(ENDED_KEY); } catch (e) { return null; }
  }

  function rotateSid() {
    // Remove the key rather than writing a new value: sid() mints one on next read, so
    // there is exactly one place that knows how a session id is made.
    try { sessionStorage.removeItem(SID_KEY); } catch (e) {}
    turnIndex = 0;
    lastModel = null;
    lastList = [];
    lastFilter = null;
  }

  function fmtTime(d) {
    var h = d.getHours(), m = d.getMinutes();
    var ap = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return h + ":" + (m < 10 ? "0" : "") + m + " " + ap;
  }

  function addSys(text) {
    var d = document.createElement("div");
    d.className = "sys";
    d.textContent = text;
    logEl.appendChild(d);
    logEl.scrollTop = logEl.scrollHeight;
    return d;
  }

  // POSTs are fire-and-forget on purpose: a rating is a courtesy the customer has already
  // paid for with a click, and blocking the UI on it — or showing them an error about our
  // telemetry — spends their goodwill to tell them something they cannot act on.
  function postFeedback(body) {
    try {
      return fetch(API + "/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      }).catch(function () {});
    } catch (e) { return null; }
  }

  function renderRatingCard() {
    var card = document.createElement("div");
    card.id = "john-rate";
    card.innerHTML =
      '<button class="dis" aria-label="Dismiss rating">×</button>' +
      '<div class="rt">Share your rating &amp; feedback</div>' +
      '<div class="faces" role="group" aria-label="Rate this session">' +
        '<button class="face" data-r="1" aria-pressed="false" aria-label="Sad" title="Sad">\uD83D\uDE1E</button>' +
        '<button class="face" data-r="2" aria-pressed="false" aria-label="Neutral" title="Neutral">\uD83D\uDE10</button>' +
        '<button class="face" data-r="3" aria-pressed="false" aria-label="Happy" title="Happy">\uD83D\uDE04</button>' +
      '</div>' +
      '<div class="cap">Please rate this support session as<br>Sad/Neutral/Happy</div>' +
      '<div class="cmt">' +
        '<textarea rows="2" maxlength="1000" placeholder="Type in your feedback here" aria-label="Your feedback"></textarea>' +
        '<button class="go" type="button">Send feedback</button>' +
      '</div>';
    logEl.appendChild(card);
    logEl.scrollTop = logEl.scrollHeight;

    var cmt = card.querySelector(".cmt");
    var cta = card.querySelector(".cmt textarea");
    var go  = card.querySelector(".cmt .go");
    var cap = card.querySelector(".cap");

    // 🔴 ONE PAYLOAD OBJECT, POSTED WHOLE EVERY TIME — NOT A DELTA. The server upserts on
    // session_id, and chat_session_rating.rating is NOT NULL. A second POST carrying only
    // {session_id, comment} would upsert rating to NULL and be REJECTED by the column
    // constraint — so the score would land and the comment would silently never attach,
    // which reads from the dashboard as 'customers rate but never explain'. Sending the
    // accumulated object makes both writes complete rows and makes ordering irrelevant.
    var payload = {
      session_id: endedSid(),
      page_context: location.pathname,
      turn_count: endedTurns,
      build_version: WIDGET_BUILD
    };

    card.querySelector(".dis").addEventListener("click", function () { card.remove(); });

    Array.prototype.forEach.call(card.querySelectorAll(".face"), function (btn) {
      btn.addEventListener("click", function () {
        var r = parseInt(btn.getAttribute("data-r"), 10);
        Array.prototype.forEach.call(card.querySelectorAll(".face"), function (b) {
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        // 🔴 THE SCORE IS SENT ON THE CLICK, BEFORE ANY COMMENT IS TYPED. Abandoning the
        // comment box is the common case, and a design that only writes on 'Send feedback'
        // would silently discard most of the ratings it collected. The comment is a second,
        // separate write that upserts onto this row — which is why service_role needs
        // UPDATE on chat_session_rating and not just INSERT.
        payload.rating = r;
        postFeedback(payload);
        if (r === 3) {
          // Happy: done in one click. Asking a satisfied customer to explain themselves is
          // how you turn a 3-second interaction into an abandoned one.
          cmt.classList.remove("show");
          cap.textContent = "Thanks — glad that helped.";
        } else {
          // Sad or Neutral: reveal the box. This is what Zendesk does and what the evidence
          // supports — brevity for the happy path, the 'why' from the people who have one.
          cmt.classList.add("show");
          cap.textContent = "Thanks. What went wrong?";
          setTimeout(function () { try { cta.focus(); } catch (e) {} }, 40);
        }
      });
    });

    go.addEventListener("click", function () {
      var t = (cta.value || "").trim();
      if (!t) return;
      go.disabled = true; cta.disabled = true;
      payload.comment = t;
      postFeedback(payload);
      cmt.classList.remove("show");
      cap.textContent = "Thanks — that goes straight to the team.";
    });
  }

  var endedTurns = 0;

  function endChat() {
    if (ended) return;
    ended = true;
    endedTurns = turnIndex;
    // Capture, THEN rotate. See the block comment above — this order is the feature.
    try { sessionStorage.setItem(ENDED_KEY, sid()); } catch (e) {}
    rotateSid();
    setBusy(true);
    addSys("You have ended this chat session " + fmtTime(new Date()));
    // Only offer a rating if there was something to rate. Ending an empty panel and being
    // asked how it went is the kind of prompt that trains people to dismiss prompts — and
    // the endpoint would refuse the row anyway, since the sid has no answer_log rows.
    if (endedTurns > 0) renderRatingCard();
    var res = document.createElement("div");
    res.id = "john-resume";
    res.innerHTML = 'Your chat session has ended. If you wish to<br>continue the chat, <a id="john-resume-a" role="button" tabindex="0">click here</a>.';
    logEl.appendChild(res);
    logEl.scrollTop = logEl.scrollHeight;
    var a = res.querySelector("#john-resume-a");
    a.addEventListener("click", resumeChat);
    a.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); resumeChat(); }
    });
    endBtn.disabled = true;
  }

  function resumeChat() {
    if (!ended) return;
    ended = false;
    // The sid was already rotated at end time, so the next turn is a genuinely new
    // session. Nothing to rotate here; just clear the ended marker and the transcript.
    try { sessionStorage.removeItem(ENDED_KEY); } catch (e) {}
    logEl.innerHTML = "";
    greeted = false;
    setBusy(false);
    endBtn.disabled = false;
    greet();
    setTimeout(function () { try { ta.focus(); } catch (e) {} }, 60);
  }

  // renderTurnstile() is called HERE and not at load: the challenge host is inside the panel,
  // which is display:none until this runs. Idempotent, so reopening does not re-render.
  function open() { panel.classList.add("open"); fab.style.display = "none"; greet(); stampServerBuild(); renderTurnstile(); setTimeout(function () { ta.focus(); }, 60); }
  // 🔴 THE × MUST CLEAR THE ANCHOR. Measured on the live asset 2026-09-03: close() hid the
  // panel and reset NOTHING — sid() lives in sessionStorage (john_sid) and turnIndex keeps
  // incrementing, so close-then-reopen has always been the SAME session. Harmless while John
  // was stateless; the moment context is carried it becomes a trap — close, reopen, ask
  // "is it TRMS?", and get the product from before you closed it.
  // sid() and turnIndex are deliberately left alone: that is existing behaviour and a
  // separate decision. Only the thing this session added is cleared.
  function close() { panel.classList.remove("open"); fab.style.display = "flex"; lastModel = null; lastList = []; lastFilter = null; }

  fab.addEventListener("click", open);
  panel.querySelector(".cl").addEventListener("click", close);
  endBtn.addEventListener("click", endChat);
  form.addEventListener("submit", function (e) { e.preventDefault(); var v = ta.value; ta.value = ""; ta.style.height = "auto"; send(v); });
  ta.addEventListener("input", function () { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 96) + "px"; });
  ta.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); }
  });

  initTurnstile();
})();
